import {
  Authenticator,
  Flex,
  Image,
  Heading,
  translations,
  useTheme,
  useAuthenticator,
  Button,
  View,
} from '@aws-amplify/ui-react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth, I18n } from 'aws-amplify';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ForgotPassordRequestSuccessPage } from './ForgotPassordRequestSuccessPage';
import { signIn, signOut } from './authenticatorSlice';

import { setSignInState } from '@/app/api/baseApi';
import { useLazyUserByUsernameQuery } from '@/app/api/generated';
import type { RootState } from '@/app/store';
import ElcompathLogo from '@/assets/images/elcompath_icon.svg';
import { authenticationMethodType } from '@/constants';
import { reloadPage } from '@/utils/reloadPage';

// バックエンドからのエラーメッセージは英語表記になる。このエラー処理関数は上書きできない。

I18n.putVocabularies(translations);
I18n.setLanguage('ja');
I18n.putVocabularies({
  ja: {
    'Send code': '再設定用のリンクを送る',
    'Send Code': '再設定用のリンクを送る',
  },
});
I18n.putVocabulariesForLanguage('ja', {
  'Forgot your password?': 'パスワードをお忘れの方',
});

interface FormData {
  username: string;
  password: string;
}

let isCompleteSendResetPassword = false;

export function AmplifyAuthenticator() {
  const { route } = useAuthenticator((context) => [context.route]);
  const dispatch = useDispatch();
  const { serviceProfile } = useSelector(
    (state: RootState) => state.serviceProfile
  );
  const [fetchUserByUsername] = useLazyUserByUsernameQuery();

  const navigate = useNavigate();

  useEffect(() => {
    if (route === 'authenticated') {
      Auth.currentAuthenticatedUser()
        .then(async (userData: CognitoUser) => {
          setSignInState(true);
          const username = userData.getUsername();
          fetchUserByUsername({ username })
            .unwrap()
            .then((user) => {
              const group = user.groups.find(
                (group) => group.id === user.users[0].usersGroups[0].groupId
              );

              dispatch(signIn({ username, userGroupName: group?.name }));
              navigate('/');
            })
            .catch((error) => {
              console.error('rejected', error);
              dispatch(signOut());
              navigate('/');
            });
        })
        .catch(() => {
          console.error('Not signed in');
          dispatch(signOut());
          // TODO: safariでnavigate(0)が動作しないときがあるため、暫定的にリロードに置き換え。
          //navigate(0);
          reloadPage();
        });
    } else {
      dispatch(signOut());
    }
  }, [route, navigate, dispatch, fetchUserByUsername]);

  const components = {
    Header() {
      const { tokens } = useTheme();

      return (
        <View
          textAlign="center"
          padding={tokens.space.large}
          backgroundColor={tokens.colors.white}
          borderRadius={tokens.radii.large}
          fontSize={tokens.fontSizes.xxxxl}
        >
          <Image
            alt="Elcompath Logo"
            src={ElcompathLogo}
            width="248px"
            height="60px"
          />
        </View>
      );
    },
    Footer() {
      const { tokens } = useTheme();

      return (
        <>
          {serviceProfile.cuconConfig.authenticationMethod !==
            authenticationMethodType.TOKEN && (
            <View textAlign="center" padding={tokens.space.xxxl}>
              <Button
                color={`${tokens.colors.neutral['80']}`}
                onClick={() => navigate(-1)}
              >
                戻る
              </Button>
            </View>
          )}
        </>
      );
    },
    SignIn: {
      Header() {
        const { tokens } = useTheme();

        return (
          <Heading
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
            level={3}
            data-testid="sign-in-header"
          >
            地図画面にサインイン
          </Heading>
        );
      },
    },
    ResetPassword: {
      Header() {
        return <Heading level={3}>パスワード再設定</Heading>;
      },
    },
  };

  const services = {
    async handleSignIn(formData: FormData) {
      const { username, password } = formData;

      return await Auth.signIn({
        username,
        password,
      });
    },
    async handleForgotPassword(email: string) {
      return await Auth.forgotPassword(email).then((result) => {
        isCompleteSendResetPassword = true;

        return result;
      });
    },
  };

  const formFields = {
    signIn: {
      username: {
        labelHidden: false,
        label: 'メールアドレスまたはユーザー名',
        isRequired: true,
        placeholder: '',
        'data-testid': 'sign-in-username-input',
      },
      password: {
        labelHidden: false,
        isRequired: true,
        placeholder: '',
        'data-testid': 'sign-in-password-input',
      },
    },
    resetPassword: {
      username: {
        labelHidden: false,
        label: 'メールアドレス',
        placeholder: 'youraddress@example.jp',
      },
    },
  };

  const renderAfterSubmit = () => {
    return <ForgotPassordRequestSuccessPage />;
  };

  const renderAuth = () => {
    return (
      <Flex direction={{ base: 'column' }} justifyContent="flex-start">
        <View>
          <Authenticator
            components={components}
            formFields={formFields}
            hideSignUp={true}
            loginMechanisms={['username', 'email']}
            services={services}
          ></Authenticator>
        </View>
      </Flex>
    );
  };

  return isCompleteSendResetPassword ? renderAfterSubmit() : renderAuth();
}
